import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type SetupLink, type UserDetail } from '../lib/api';
import { ErrorNote, SectionHeading, SetupLinkPanel, Spinner } from '../components/ui';
import { Related } from '../components/Related';
import { DeleteRecord } from '../components/DeleteRecord';
import { Confirm } from '../components/Confirm';
import { useIsPlatformAdmin } from '../lib/useSession';

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * One staff record, and the classes they take.
 *
 * The Staff table reported a class count and gave no way to see or change what
 * it counted, so assigning somebody meant opening each class in turn and
 * finding them in its picker.
 *
 * Assignment is the whole of a teacher's authorization: they can open exactly
 * the registers listed here. An administrator reaches every class regardless,
 * which is why this section says so rather than looking like a restriction.
 */
export function ManageUserDetail() {
  const { userId } = useParams({ from: '/app/manage/users/$userId' });
  const queryClient = useQueryClient();

  const detail = useQuery({ queryKey: ['user', userId], queryFn: () => api.admin.user(userId) });
  const classes = useQuery({ queryKey: ['classes'], queryFn: api.classes.list });

  /*
   * ['classes'] is not in here: its columns count students and periods, and
   * neither changes when staff do. ['class'] is, because the class detail page
   * lists its own staff.
   */
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['user', userId] }),
      queryClient.invalidateQueries({ queryKey: ['users'] }),
      queryClient.invalidateQueries({ queryKey: ['class'] }),
    ]);
  };

  const [query, setQuery] = useState('');
  const [link, setLink] = useState<SetupLink | null>(null);

  /* One request per class, in sequence, for the reason the period page gives. */
  const assign = useMutation({
    mutationFn: async (classIds: string[]) => {
      for (const classId of classIds) await api.admin.assignTeacher(classId, userId);
    },
    onSettled: refresh,
    onSuccess: () => setQuery(''),
  });

  const unassign = useMutation({
    mutationFn: (classTeacherId: string) => api.admin.unassignTeacher(classTeacherId),
    onSuccess: refresh,
  });

  const sendLink = useMutation({
    mutationFn: () => api.admin.setupLink(userId),
    onSuccess: async (data) => {
      setLink(data);
      await refresh();
    },
  });

  if (detail.isLoading) return <Spinner />;
  if (detail.isError || !detail.data) {
    return (
      <div>
        <Link to="/manage/users" className="eyebrow">
          Back to staff
        </Link>
        <div className="mt-4">
          <ErrorNote message={message(detail.error, 'Could not load this staff member.')} />
        </div>
      </div>
    );
  }

  const user = detail.data;
  const assignedIds = new Set(user.classes.map((row) => row.classId));
  const candidates = (classes.data?.classes ?? [])
    .filter((klass) => klass.isActive && !assignedIds.has(klass.id))
    .map((klass) => ({ id: klass.id, label: klass.name }));

  return (
    <div className="grid gap-8">
      <div>
        <Link to="/manage/users" className="eyebrow hover:text-ink">
          Back to staff
        </Link>
        <div className="mt-2">
          <UserHeader
            user={user}
            onDone={refresh}
            onLink={() => sendLink.mutateAsync()}
            linking={sendLink.isPending}
          />
        </div>
        {link ? (
          <div className="mt-3">
            <SetupLinkPanel
              url={link.url}
              expiresAt={link.expiresAt}
              onDismiss={() => setLink(null)}
            />
          </div>
        ) : null}
      </div>

      <Related
        title="Takes"
        description={
          user.role === 'teacher'
            ? 'The registers this account can open. A teacher sees exactly these and nothing else.'
            : 'Administrators can already open every class, so this changes what they are listed against rather than what they can reach.'
        }
        count={`${user.classes.length} assigned`}
        error={unassign.error ? message(unassign.error, 'Could not remove that class.') : null}
        label="Assign a class"
        options={candidates}
        query={query}
        onQuery={setQuery}
        empty="Already assigned to every active class."
        searchPlaceholder="class name"
        disabled={assign.isPending}
        verb="Assign"
        applying={assign.isPending}
        note={
          assign.error ? (
            <div className="mt-3">
              <ErrorNote
                message={message(
                  assign.error,
                  'Could not assign every class. The list above shows which ones took.',
                )}
              />
            </div>
          ) : null
        }
        onApply={(ids, done) => assign.mutate(ids, { onSuccess: done })}
      >
        <ul className="mb-4">
          {user.classes.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center gap-3 border-b border-rule py-2 text-sm last:border-0"
            >
              <Link
                to="/manage/classes/$classId"
                params={{ classId: row.classId }}
                className={
                  row.isActive ? 'hover:underline' : 'text-ink-faint line-through hover:text-ink'
                }
              >
                {row.name}
              </Link>
              {row.isActive ? null : <span className="eyebrow">retired</span>}
              <span className="ml-auto">
                <Confirm
                  trigger="Remove"
                  title={`Remove ${user.name} from ${row.name}?`}
                  description="They can no longer open that register. Marks they made on it are kept."
                  confirmLabel="Remove"
                  pendingLabel="Removing…"
                  disabled={unassign.isPending}
                  onConfirm={() => unassign.mutateAsync(row.id)}
                  errorFallback="Could not remove that class."
                />
              </span>
            </li>
          ))}
          {user.classes.length === 0 ? (
            <li className="py-1.5 text-sm text-ink-soft">
              {user.role === 'teacher'
                ? 'No classes assigned, so this account can open no registers.'
                : 'No classes assigned. An administrator can still open every one.'}
            </li>
          ) : null}
        </ul>
      </Related>
    </div>
  );
}

/**
 * The account's own details.
 *
 * This used to live under the row in the Staff list. The password is never
 * shown or set here: a setup link lets the holder choose their own, which is
 * what avoids inventing a temporary one and sending it somewhere.
 */
function UserHeader({
  user,
  onDone,
  onLink,
  linking,
}: {
  user: UserDetail;
  onDone: () => void;
  /** Asks first; the panel waits on the promise. */
  onLink: () => Promise<unknown>;
  linking: boolean;
}) {
  const navigate = useNavigate();
  const canRestore = useIsPlatformAdmin();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email ?? '');
  const [role, setRole] = useState(user.role);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.admin.updateUser(user.id, body),
    onSuccess: onDone,
  });

  /* Separate from `save`, for the reason the other record pages give. */
  const remove = useMutation({
    mutationFn: (isDeleted: boolean) => api.admin.updateUser(user.id, { isDeleted }),
    onSuccess: async (_result, isDeleted) => {
      onDone();
      if (isDeleted) await navigate({ to: '/manage/users' });
    },
  });

  const dirty = name !== user.name || email !== (user.email ?? '') || role !== user.role;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-1.5">
            {user.role === 'org_admin' ? 'Administrator' : 'Teacher'}
          </p>
          <h1 className="text-[28px] leading-none font-semibold tracking-[-0.02em]">{user.name}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {user.isActive ? null : <span className="eyebrow">deactivated</span>}
          {user.pendingSetup ? <span className="eyebrow">setup pending</span> : null}
          <Confirm
            trigger={user.pendingSetup ? 'New link' : 'Reset password'}
            title={`Send ${user.name} a new setup link?`}
            description="Any setup link already issued to them stops working. Their current password keeps working until they use the new one."
            confirmLabel={user.pendingSetup ? 'New link' : 'Reset password'}
            pendingLabel="Creating…"
            disabled={!user.isActive || linking}
            onConfirm={onLink}
            errorFallback="Could not create a setup link."
          />
          {user.deletedAt ? null : user.isActive ? (
            <Confirm
              trigger="Deactivate"
              title={`Deactivate ${user.name}?`}
              description="They can no longer sign in. Anything they marked stays attributed to them."
              confirmLabel="Deactivate"
              pendingLabel="Deactivating…"
              disabled={save.isPending}
              onConfirm={() => save.mutateAsync({ isActive: false })}
              errorFallback="Could not deactivate this account."
            />
          ) : (
            <button
              type="button"
              className="btn"
              disabled={save.isPending}
              onClick={() => save.mutate({ isActive: true })}
            >
              Reactivate
            </button>
          )}
          {/* No mark count: an account's past marks are attributed by a
              nullable reference that no report joins, so deleting one hides no
              attendance and rewrites no figures. */}
          {user.isActive ? null : (
            <DeleteRecord
              noun="account"
              deleted={Boolean(user.deletedAt)}
              canRestore={canRestore}
              onDelete={() => remove.mutateAsync(true)}
              onRestore={() => remove.mutate(false)}
              pending={remove.isPending}
              error={remove.error ? message(remove.error, 'Could not change that.') : null}
            />
          )}
        </div>
      </div>

      <section>
        <SectionHeading
          title="Details"
          description="An account is deactivated rather than deleted, so the marks it took stay attributed."
        />
        <form
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ name, email, role });
          }}
        >
          {save.error ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <ErrorNote message={message(save.error, 'Could not save.')} />
            </div>
          ) : null}
          <label className="grid gap-1.5">
            <span className="eyebrow">Name</span>
            <input
              className="field"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Email</span>
            <input
              className="field font-mono text-xs"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Role</span>
            <select
              className="field"
              value={role}
              onChange={(e) => setRole(e.target.value as UserDetail['role'])}
            >
              <option value="teacher">Teacher - only assigned classes</option>
              <option value="org_admin">Administrator - everything</option>
            </select>
          </label>
          <div className="sm:col-span-2 lg:col-span-3">
            <button className="btn btn-primary" type="submit" disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
